import { NullableTableColumns, useInfiniteScrollTable } from "@flaminem/ui-components";
import { FC, ReactNode, useMemo } from "react";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { InfiniteScrollTable } from "src/components/Tables/InfiniteScrollTable";
import { defaultRowHeight } from "src/config/table-config";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import { HeadlessWidgetRenderer } from "src/dynamic-view-renderer/headlessWidgets/HeadlessWidgetRenderer";
import { useT } from "src/internationalization/useT";
import { LinkedCase } from "src/types/case";

export type RelatedCasesTableProps = {
  cases: LinkedCase[];
  loading: boolean;
  isError: boolean;
  caseTypeCode?: string;
  /**
   * If true, uses full-height layout (for full-page tables).
   * If false, uses flexible height (for modals/constrained containers).
   * @default true
   */
  fullHeight?: boolean;
  totalCount?: number;
  fetchMore?: (startIndex: number, stopIndex: number) => void;
  pageOffset?: number;
  toolbar?: () => ReactNode;
};

export const RelatedCasesTable: FC<RelatedCasesTableProps> = ({
  cases,
  isError,
  loading,
  caseTypeCode,
  fullHeight = true,
  totalCount,
  fetchMore,
  pageOffset = 0,
  toolbar,
}) => {
  const t = useT();
  const { caseTypeDefinitions, headlessWidgets } = useDynamicViewSchema();
  const { pageSize, dimensions } = useDimensions();

  const resolvedCaseTypeCode = caseTypeCode ?? cases[0]?.typeCode;
  const resolvedTotalCount = totalCount ?? cases.length;

  const columns: NullableTableColumns<LinkedCase> = useMemo(() => {
    // Find the case type configuration
    const caseTypeConfig = resolvedCaseTypeCode
      ? caseTypeDefinitions?.find((ct) => ct.code === resolvedCaseTypeCode)
      : undefined;

    const caseListTable = caseTypeConfig?.caseListTable;

    if (!caseListTable) {
      return {};
    }

    const dynamicColumns = caseListTable.columns.reduce((acc, column) => {
      const widget = headlessWidgets.find((w) => w.code === column.headlessWidgetCode);
      if (!widget) return acc;

      return {
        ...acc,
        [column.headlessWidgetCode]: {
          label: column.headerLabel,
          flexGrow: column.flexGrow ?? (column.width && column.width !== 1 ? 0 : 1),
          flexShrink: column.flexShrink ?? (column.width && column.width !== 1 ? 0 : 1),
          width: column.width || 1,
          className: "text-sm",
          render: (row: LinkedCase) => (
            <HeadlessWidgetRenderer
              widget={widget}
              data={{
                root: row,
                elements: row.elements,
                propertyValues: row.properties,
                caseTypeDefinitions: caseTypeDefinitions,
              }}
            />
          ),
          sorter: column.sorter,
        },
      };
    }, {});

    return dynamicColumns;
  }, [resolvedCaseTypeCode, caseTypeDefinitions, headlessWidgets]);

  const { data, totalCount: syncedTotalCount } = useInfiniteScrollTable<LinkedCase>(
    cases as LinkedCase[],
    resolvedTotalCount,
    pageSize,
    pageOffset
  );

  const modalHeightClass = fullHeight ? undefined : "flex-1 min-h-0";

  return (
    <DimensionedLayout
      fullHeight={fullHeight}
      className={fullHeight ? undefined : modalHeightClass}
    >
      {dimensions?.width && (
        <InfiniteScrollTable
          id="related-cases-table"
          title={t("cases.relatedCases.table.title")}
          description={t("cases.relatedCases.table.description")}
          data={data}
          columns={columns}
          totalCount={syncedTotalCount}
          pageSize={pageSize}
          rowHeight={defaultRowHeight}
          isLoading={loading}
          isError={isError}
          noRowsText={t("general.table.empty")}
          fetchMore={fetchMore ?? (() => undefined)}
          toolbar={toolbar}
        />
      )}
    </DimensionedLayout>
  );
};
